export const EDIT_LIST_SORT_MOVE_DURATION = 280;

export function createEditListDragState() {
	return {
		dragId: '',
		dragOverId: '',
	};
}

export function resolveEditListSortContainer(ref) {
	if (!ref) return null;
	return ref.$el || ref;
}

export function resolveEditListSortTargetFromEvent(event, containerRef) {
	const containerEl = resolveEditListSortContainer(containerRef);
	if (!containerEl || !event) return '';

	const el = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-sort-id]');
	if (!el || !containerEl.contains(el)) return '';

	return el.dataset.sortId || '';
}

export function captureEditListSortRects(containerEl, items) {
	const map = new Map();
	if (!containerEl) return map;
	// 构建 items 中实际存在的 id 集合
	const validIds = new Set(items.map(item => item?.id));
	Array.from(containerEl.children).forEach(el => {
		const id = el.dataset?.sortId;
		// 只捕获 items 数组中实际存在的元素
		if (id && validIds.has(id)) {
			map.set(id, el.getBoundingClientRect());
		}
	});
	return map;
}

export function cancelEditListSortFlipAnimations(containerEl) {
	if (!containerEl) return;
	Array.from(containerEl.children).forEach(el => {
		el.style.transition = '';
		el.style.transform = '';
		el.style.willChange = '';
	});
}

export function animateEditListSortFlip(containerEl, prevRects, duration = EDIT_LIST_SORT_MOVE_DURATION, skipId = '') {
	if (!containerEl || !prevRects?.size) return;

	Array.from(containerEl.children).forEach(el => {
		const id = el.dataset?.sortId;
		if (!id || id === skipId) return;

		const prev = prevRects.get(id);
		if (!prev) return;

		const next = el.getBoundingClientRect();
		const dx = prev.left - next.left;
		const dy = prev.top - next.top;
		if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

		el.style.transition = 'none';
		el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
		el.style.willChange = 'transform';

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				el.style.transition = `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
				el.style.transform = 'translate3d(0, 0, 0)';
			});
		});

		const cleanup = () => {
			el.style.transition = '';
			el.style.transform = '';
			el.style.willChange = '';
		};

		el.addEventListener('transitionend', event => {
			if (event.propertyName === 'transform') {
				cleanup();
			}
		}, { once: true });
		window.setTimeout(cleanup, duration + 80);
	});
}

export function runEditListSortFlip(vm, containerRef, prevRects, duration, skipId = '') {
	const run = () => {
		animateEditListSortFlip(
			resolveEditListSortContainer(containerRef),
			prevRects,
			duration,
			skipId,
		);
	};
	if (vm && typeof vm.$nextTick === 'function') {
		vm.$nextTick(run);
		return;
	}
	Promise.resolve().then(run);
}

export function reorderListItemByDrag(items, dragId, targetId) {
	const fromIndex = items.findIndex(item => item.id === dragId);
	const toIndex = items.findIndex(item => item.id === targetId);
	if (!dragId || dragId === targetId) return null;
	if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return null;
	const [moved] = items.splice(fromIndex, 1);
	items.splice(toIndex, 0, moved);
	return { fromIndex, toIndex };
}

export function commitEditListSortDrag(vm, containerRef, items, dragState, onMoved) {
	if (!dragState?.dragId || !dragState?.dragOverId || dragState.dragId === dragState.dragOverId) {
		return null;
	}

	const dragId = dragState.dragId;
	const containerEl = resolveEditListSortContainer(containerRef);
	cancelEditListSortFlipAnimations(containerEl);
	const prevRects = captureEditListSortRects(containerEl, items);
	const result = reorderListItemByDrag(items, dragId, dragState.dragOverId);
	if (!result) return null;

	runEditListSortFlip(vm, containerRef, prevRects, EDIT_LIST_SORT_MOVE_DURATION, dragId);
	if (typeof onMoved === 'function') {
		onMoved(result);
	}
	return result;
}

export function handleEditListSortDragStart(dragState, id) {
	dragState.dragId = id;
	dragState.dragOverId = '';
}

export function handleEditListSortDragOver(event, containerRef, dragState) {
	event.preventDefault();
	if (!dragState?.dragId) return;

	dragState._lastDragOverEvent = event;

	const targetId = resolveEditListSortTargetFromEvent(event, containerRef);
	if (!targetId || targetId === dragState.dragId) return;

	dragState.dragOverId = targetId;
}

export function handleEditListSortDragEnd(vmOrDragState, containerRef, items, onMoved) {
	const dragState = vmOrDragState;
	const validIds = new Set(items.map(item => item?.id));
	if (dragState.dragOverId && !validIds.has(dragState.dragOverId)) {
		dragState.dragOverId = '';
	}

	if (!dragState.dragOverId && containerRef) {
		const lastEvent = dragState._lastDragOverEvent;
		if (lastEvent) {
			const targetId = resolveEditListSortTargetFromEvent(lastEvent, containerRef);
			if (targetId && validIds.has(targetId)) {
				dragState.dragOverId = targetId;
			}
		}
	}

	try {
		return commitEditListSortDrag(vmOrDragState, containerRef, items, dragState, onMoved);
	} finally {
		clearEditListSortDragState(dragState);
	}
}

export function clearEditListSortDragState(dragState) {
	if (!dragState) return;
	dragState.dragId = '';
	dragState.dragOverId = '';
	dragState._lastDragOverEvent = null;
}

export function resolveSelectedIndexAfterListMove(selectedIndex, fromIndex, toIndex) {
	if (selectedIndex == null) return selectedIndex;
	if (selectedIndex === fromIndex) return toIndex;
	if (fromIndex < selectedIndex && toIndex >= selectedIndex) return selectedIndex - 1;
	if (fromIndex > selectedIndex && toIndex <= selectedIndex) return selectedIndex + 1;
	return selectedIndex;
}

export function getEditListSortItemClass(dragId, itemId, dragOverId = '') {
	return {
		'g-edit-list-sort-item': true,
		'is-list-sort-dragging': Boolean(dragId && dragId === itemId),
	};
}
